import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import ScheduleService from 'services/Schedule'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import Select from 'components/Inputs/Select'
import SpecialtySelect from 'components/Inputs/SpecialtySelect'
import MoneyInput from 'components/Inputs/MoneyInput'
import NumberInput from 'components/Inputs/NumberInput'
import CheckBox from 'components/CheckBox'
import Tooltip from 'components/Tooltip'
import SyncServicesModal from '../SyncServicesModal'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import submitOnEnter from 'utils/submitOnEnter'
import createServiceValidator from 'validators/createServiceValidator'
import { FaPlus, FaTrash } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  ProductGrid,
  Input,
  TextArea,
  Row,
  CheckRow,
  Label,
  InfoText,
  WarningText,
  ErrorContainer
} from './styles'

export default function AddServiceModal ({
  visible,
  weekdays = [],
  professionals = [],
  businessProducts = [],
  serviceCategories = [],
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const [addPromotion, setAddPromotion] = useState (false)
  const [showSyncDialog, setShowSyncDialog] = useState (false)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const calculateComissionValue = (price, comissionPercentage) => {
    return Math.round (price * (comissionPercentage / 100 || 0) * 100) / 100
  }

  const calculateComissionPercentage = (price, comissionValue) => {
    return Math.round ((comissionValue / price) * 100 * 100) / 100
  }

  const recalcutaleComissionValue = (values, setFieldValue) => {
    const comissionValue = calculateComissionValue (values.price, values.comission_percentage)
    setFieldValue ('professional_comission', comissionValue)
  }

  const toggleSyncDialog = () => setShowSyncDialog (!showSyncDialog)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='lg' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Cadastrar serviço</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={createServiceValidator}
          initialValues={{
            'name': '',
            'description': '',
            'category': null,
            'specialty': null,
            'duration': 30,
            'max_delay': 5,
            'price': 0,
            'comission_percentage': 40,
            'professional_comission': 0,
            'business_comission': 0,
            'accepts_online': false,
            'promotion_weekdays': weekdays.map (day => ({
              ...day, price: 0, selected: false
            })),
            'products': [],
            'professionals': []
          }}
          onSubmit={async (values, { setSubmitting }) => {
            const { category, specialty, comission_percentage, ...serviceData } = values
            let promotion = null

            try {
              const { data: service } = await cutileiApi.post ('/services', {
                serviceCategoryId: category.value,
                specialty_id: specialty?.value ?? null,
                businessId: businessId,
                comission_percentage: comission_percentage / 100,
                ...serviceData
              }, requestConfig)

              if (addPromotion) {
                const { data: promotionData } = await cutileiApi.post (`/businesses/${businessId}/promotions`, {
                  serviceId: service.id,
                  weekdays: values.promotion_weekdays.filter (day => day.selected)
                }, requestConfig)

                promotion = promotionData
              }

              setSubmitting (false)
              handleConfirm ({
                ...service,
                products: values.products,
                promotions: [promotion]
              })
              handleClose ()
            } catch (error) {
              setSubmitting (false)
              setErrorMessage (getApiErrorMessage (error))
              logApiError (error)
            }
          }}
        >
          {function AddServiceForm ({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit
          }) {
            useEffect (() => {
              recalcutaleComissionValue (values, setFieldValue)
            }, [values.price])

            useEffect (() => {
              setFieldValue ('business_comission', Math.abs (values.price - values.professional_comission))
            }, [values.price, values.comission_percentage, values.professional_comission])

            const handleAddProduct = () => {
              setFieldValue ('products', [
                ...values.products, {
                  id: '',
                  name: '',
                  quantity_used: 1
                }
              ])
            }

            const handleDeleteProduct = index => {
              setFieldValue (
                'products',
                values.products.map ((p, i) => ({...p, idx: i})).filter (s => s.idx !== index)
              )
            }

            const handleSave = professionals.length > 0 ? toggleSyncDialog : handleSubmit

            return (
              <>
                <DialogBody>
                  <Form onKeyDown={submitOnEnter(handleSave, isSubmitting)}>
                    <Row>
                      <FormField>
                        <Input 
                          placeholder='Nome do serviço'
                          value={values.name}
                          onChange={handleChange ('name')} 
                          onBlur={handleBlur ('name')}
                        />
                        {touched.name && errors.name && (
                          <WarningText>
                            {errors.name}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <Select
                          name='category'
                          placeholder='Categoria...'
                          value={values.category}
                          options={serviceCategories}
                          onChange={(name, option) => {
                            setFieldValue (name, option)
                            setFieldValue ('specialty', null)
                          }}
                          onBlur={setFieldTouched}
                          error={errors.category}
                          touched={touched.category}
                        />
                        {touched.category && errors.category && (
                          <WarningText>
                            {errors.category}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <Row>
                      <FormField>
                        <SpecialtySelect
                          categoryId={values.category?.value}
                          value={values.specialty}
                          onChange={option => setFieldValue ('specialty', option)}
                        />
                      </FormField>
                    </Row>
                    <Row style={{width: 480, alignSelf: 'center'}}>
                      <FormField>
                        <Label htmlFor='duration'>
                          Tempo do serviço (mins) {values.duration >= 60 && ` - ${
                            ScheduleService.getFormattedDuration (values.duration)
                          }`}
                        </Label>
                        <NumberInput
                          id='duration'
                          name='duration'
                          value={values.duration}
                          minValue={15}
                          stepOnly={true}
                          step={values.duration < 60 ? 15 : 30}
                          onChange={(name, value) => {
                            const newValue = values.duration === 60 && values.duration > value ? 45 : value
                            setFieldValue (name, newValue)
                          }}
                          style={{width: '100%'}}
                        />
                      </FormField>
                      <FormField>
                        <Row style={{margin: 0, gap: 3}}>
                          <Label htmlFor='max_delay'>
                            Tolerância de atraso (mins)
                          </Label>
                          <Tooltip
                            id='max_delay_tooltip'
                            place='bottom'
                            style={{marginTop: -1}}
                            message={
                              <Label style={{color: '#FFFFFF', width: 300}}>
                                A tolerância de atraso pode ser configurada da seguinte forma:<br/><br/>
                                Serviços do salão: 5 a 20 minutos<br/>
                                Serviços de Pé e mão Cutilei: 10 a 15 minutos<br/>
                                Demais Serviços do Cutilei: 7 a 15 minutos<br/>
                              </Label>
                            }
                          />
                        </Row>
                        <NumberInput
                          id='max_delay'
                          name='max_delay'
                          value={values.max_delay}
                          minValue={5}
                          maxValue={20}
                          onChange={setFieldValue}
                          style={{width: '100%'}}
                        />
                      </FormField>
                    </Row>
                    <Row style={{alignItems: 'self-end'}}>
                      <FormField>
                        <Label htmlFor='price'>
                          Preço (R$)
                        </Label>
                        <MoneyInput
                          id='price'
                          name='price'
                          placeholder='Preço'
                          value={values.price}
                          onValueChange={setFieldValue}
                          onBlur={handleBlur ('price')}
                        />
                        {touched.price && errors.price && (
                          <WarningText>
                            {errors.price}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField>
                        <Label htmlFor='comission_percentage'>
                          Comissão (%)
                        </Label>
                        <NumberInput
                          id='comission_percentage'
                          name='comission_percentage'
                          placeholder='Comissão (%)'
                          value={values.comission_percentage}
                          minValue={0}
                          maxValue={100}
                          onChange={(name, value) => {
                            setFieldValue (name, value)
                            setFieldValue ('professional_comission', calculateComissionValue (values.price, value))
                          }}
                          onBlur={handleBlur}
                        />
                      </FormField>
                      <FormField>
                        <Label htmlFor='professional_comission'>
                          Comissão (profissional)
                        </Label>
                        <MoneyInput
                          id='professional_comission'
                          name='professional_comission'
                          placeholder='Comissão (profissional)'
                          value={values.professional_comission}
                          maxValue={values.price}
                          onValueChange={(name, value) => {
                            setFieldValue (name, value)
                            setFieldValue ('comission_percentage', calculateComissionPercentage (values.price, value))
                          }}
                          onBlur={handleBlur ('professional_comission')}
                          style={{color: '#44CF6C', fontSize: 16, fontWeight: 'bold'}}
                        />
                      </FormField>
                      <FormField>
                        <Label htmlFor='business_comission'>
                          Valor (salão)
                        </Label>
                        <MoneyInput
                          id='business_comission'
                          name='business_comission'
                          value={values.business_comission}
                          style={{color: '#44CF6C', fontSize: 16, fontWeight: 'bold'}}
                          disabled={true}
                        />
                      </FormField>
                    </Row>
                    <Row>
                      <FormField>
                        <TextArea 
                          placeholder='Descrição do serviço (opcional)'
                          value={values.description}
                          rows={3}
                          onChange={handleChange ('description')} 
                          onBlur={handleBlur ('description')}
                        />
                        {touched.description && errors.description && (
                          <WarningText>
                            {errors.description}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <FormField>
                      <CheckRow style={{marginBottom: 7}}>
                        <CheckBox
                          id='accepts_online'
                          style={{marginRight: -3}}
                          value={values.accepts_online}
                          onValueChange={value => setFieldValue ('accepts_online', value)}
                        />
                        <Label htmlFor='accepts_online'>
                          Permitir agendamento por link
                        </Label>
                      </CheckRow>
                    </FormField>
                    <FormField>
                      <CheckRow>
                        <CheckBox
                          id='add_promotion'
                          style={{marginRight: -3}}
                          value={addPromotion}
                          onValueChange={setAddPromotion}
                        />
                        <Label htmlFor='add_promotion'>
                          Adicionar promoção
                        </Label>
                      </CheckRow>
                    </FormField>
                    {addPromotion && (
                      <FormField>
                        <InfoText style={{width: 500}}>
                          Selecione os dias da semana em que deseja configurar um preço promocional para este serviço. Dias não selecionados permanecerão com o preço padrão.
                        </InfoText>
                        <Row>
                          {weekdays.map ((day, index) => (
                            <FormField key={day.id}>
                              <CheckRow style={{marginBottom: 3}}>
                                <CheckBox
                                  id={`promotion_weekdays.${index}.selected`}
                                  style={{marginRight: -3}}
                                  value={values.promotion_weekdays[index].selected}
                                  onValueChange={value => {
                                    if (value === true)
                                      setFieldValue (`promotion_weekdays.${index}.price`, values.price)
                                    setFieldValue (`promotion_weekdays.${index}.selected`, value)
                                  }}
                                />
                                <Label htmlFor={`promotion_weekdays.${index}.selected`}>
                                  {day.name.substring (0, 3)}
                                </Label>
                              </CheckRow>
                              {values.promotion_weekdays[index].selected && (
                                <MoneyInput
                                  id={`promotion_weekdays.${index}.price`}
                                  name={`promotion_weekdays.${index}.price`}
                                  placeholder='Preço'
                                  value={values.promotion_weekdays[index].price}
                                  onValueChange={setFieldValue}
                                  onBlur={handleBlur (`promotion_weekdays.${index}.price`)}
                                />
                              )}
                            </FormField>
                          ))}
                        </Row>
                      </FormField>
                    )}
                    {values.products.length > 0 && values.products.map ((product, index) => (
                      <ProductGrid key={index}>
                        <FormField>
                          <Label>Produto</Label>
                          <Select
                            name={`products.${index}.id`}
                            placeholder='Selecionar...'
                            value={product.id ? {
                              label: product.nickname || product.name,
                              value: product.id
                            } : null}
                            options={businessProducts}
                            onChange={(name, option) => {
                              setFieldValue (name, option.value.id)
                              setFieldValue (`products.${index}.name`, option.value.nickname || option.value.name)
                            }}
                            onBlur={setFieldTouched}
                            error={errors.products?.[index]?.name}
                            touched={touched.products?.[index]?.name}
                          />
                          {touched.products?.[index]?.name && errors.products?.[index]?.name && (
                            <WarningText>
                              {errors.products?.[index]?.name}
                            </WarningText>
                          )}
                        </FormField>
                        <Row style={{margin: 0, gap: 0}}>
                          <FormField>
                            <Label>Qtd. utilizada</Label>
                            <NumberInput
                              name={`products.${index}.quantity_used`}
                              value={product.quantity_used}
                              minValue={1}
                              maxValue={999999}
                              onChange={setFieldValue}
                            />
                          </FormField>
                          <InfoText style={{alignSelf: 'flex-end', marginBottom: 9}}>
                            {businessProducts.find (p => p.value.id === product.id)?.value.content_unit}
                          </InfoText>
                        </Row>
                        <MainButton
                          leftIcon={<FaTrash color='#FFFFFF' size={12}/>}
                          onClick={() => handleDeleteProduct (index)}
                          dangerous={true}
                          width={50}
                          style={{alignSelf: 'flex-end'}}
                        />
                      </ProductGrid>
                    ))}
                    <SecondaryButton
                      label='Adicionar produto'
                      leftIcon={<FaPlus size={12} color='#FFC100'/>}
                      onClick={handleAddProduct}
                      width={180}
                      style={{marginTop: 10}}
                    />
                    {errorMessage && (
                      <ErrorContainer>
                        {errorMessage}
                      </ErrorContainer>
                    )}
                    <SyncServicesModal
                      visible={showSyncDialog}
                      professionals={professionals}
                      onConfirm={professionals => {
                        setFieldValue ('professionals', professionals)
                        setTimeout (() => { handleSubmit () }, 0)
                      }}
                      onClose={toggleSyncDialog}
                    />
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <MainButton
                    label='Criar serviço'
                    onClick={handleSave}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    width={180}
                    style={{marginTop: 15}}
                  />
                </DialogFooter>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
